import type { ComponentType } from "react";

import { site } from "@/content/site";
import { buildSchemaForRoute } from "@/lib/schema";
import AboutPage from "@/routes/about/page";
import ContactPage from "@/routes/contact/page";
import FaqPage from "@/routes/faq/page";
import HomePage from "@/routes/home/page";
import HowItWorksPage from "@/routes/how-it-works/page";
import NotFoundPage from "@/routes/not-found/page";
import PrivacyPage from "@/routes/privacy/page";
import ProductPage from "@/routes/product/page";
import SecurityPage from "@/routes/security/page";
import TermsPage from "@/routes/terms/page";
import UseCasesPage from "@/routes/use-cases/page";

export type SiteRoute = {
  path: string;
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  schema: unknown;
  indexable: boolean;
  component: ComponentType;
};

function route(
  path: string,
  title: string,
  description: string,
  component: ComponentType,
  indexable = true,
): SiteRoute {
  const ogTitle = title;
  const ogDescription = description;

  return {
    path,
    title,
    description,
    ogTitle,
    ogDescription,
    schema: buildSchemaForRoute({ path, title, description }),
    indexable,
    component,
  };
}

export const routes: SiteRoute[] = [
  route(
    "/",
    "Agentic Plus | Evidence-led creative growth",
    "Agentic Plus turns product facts, brand knowledge, and market evidence into briefs, creative, approvals, and learning in one trackable workflow.",
    HomePage,
  ),
  route(
    "/product",
    "Creative growth platform | Agentic Plus",
    "Explore the Growth Agent, missions, Evidence Graph, Brand Brain, governed production, approvals, and experiment memory inside Agentic Plus.",
    ProductPage,
  ),
  route(
    "/how-it-works",
    "How Agentic Plus works | Agentic Plus",
    "See how Agentic Plus connects evidence, strategy, production, approvals, governed delivery, and reusable creative learning.",
    HowItWorksPage,
  ),
  route(
    "/use-cases",
    "Marketing use cases | Agentic Plus",
    "See how Agentic Plus supports launches, fatigue recovery, localization, competitor testing, agency onboarding, and brand-safe production.",
    UseCasesPage,
  ),
  route(
    "/faq",
    "Agentic Plus FAQ | Agentic Plus",
    "Answers about Agentic Plus scope, generation, approvals, tenant isolation, evidence retention, advertising support, and roadmap capabilities.",
    FaqPage,
  ),
  route(
    "/about",
    "About Agentic Plus",
    "Agentic Plus builds an accountable creative growth layer where evidence, production, approval, and learning remain connected.",
    AboutPage,
  ),
  route(
    "/contact",
    "Contact Agentic Plus",
    "Request an Agentic Plus demo, discuss agency onboarding, or start a security review with the Agentic Plus team.",
    ContactPage,
  ),
  route(
    "/security",
    "Security at Agentic Plus",
    "Learn how Agentic Plus handles workspace isolation, permissions, approvals, traceability, unknown states, and compensation paths.",
    SecurityPage,
  ),
  route(
    "/privacy",
    "Privacy Policy | Agentic Plus",
    "Understand what Agentic Plus processes, how customer content is used, which service providers help operate the platform, and how to contact us.",
    PrivacyPage,
  ),
  route(
    "/terms",
    "Terms of Service | Agentic Plus",
    "Review the service description, account responsibilities, acceptable use, customer materials, generated outputs, and third-party platform terms.",
    TermsPage,
  ),
  route(
    "/404",
    "Page not found | Agentic Plus",
    "The requested Agentic Plus page is unavailable. Continue to the homepage, product overview, or contact page.",
    NotFoundPage,
    false,
  ),
];

export const indexableRoutes = routes.filter((route) => route.indexable);

export function findRoute(pathname: string): SiteRoute | undefined {
  return routes.find((candidate) => candidate.path === pathname);
}

export function canonicalUrl(path: string): string {
  return `${site.url}${path === "/" ? "/" : path}`;
}
